var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { injectable } from '../../../Data/Decorators/Injectable';
import { inject } from '../../../Data/Decorators/Inject';
import { CoreRoutes } from './CoreRoutes';
import { BaseCoordinator } from './BaseCoordinator';
import { ICustomNavigatorType } from '../CustomNavigator/ICustomNavigator';
import { Alert, Platform } from 'react-native';
import * as Font from 'expo-font';
import queryString from 'query-string';
import { uuid } from '../../../Helpers/Uuid/Uuid';
import { omit } from '../../../Helpers/Objects/Objects';
import { setColorScheme } from '../../Hooks/UseColorScheme';
import { setDirection } from '../../Hooks/UseDirection';
let alreadyLoadedFonts = [];
let Coordinator = class Coordinator extends BaseCoordinator {
    constructor(customNavigator) {
        super();
        this.customNavigator = customNavigator;
        this.loaders = 0;
        this.toasts = [];
    }
    setDir(dir) {
        setDirection(dir);
    }
    getIsShowedNotificationsPermissions() {
        return this.getCurrentRoute() === CoreRoutes.notificationsPermissions;
    }
    getBehaviorFromURL(url) {
        if (!url) {
            return null;
        }
        const params = queryString.parse(url.split('?').pop() || '');
        const behavior = params['fs_payload[behavior]'];
        if (!behavior) {
            return null;
        }
        return behavior;
    }
    setColorScheme(scheme) {
        return setColorScheme(scheme);
    }
    dismissAppUpdate() {
        return this.dismiss();
    }
    navigateToAppUpdate() {
        return this.navigate(CoreRoutes.appUpdate);
    }
    getFontWeight(name) {
        const weights = {
            light: 300,
            regular: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
        };
        const word = name.split('_')[0];
        return weights[word] || weights.regular;
    }
    attachFonts(fonts, icons) {
        let allFonts = fonts.reduce((acc, font) => {
            return Object.assign(Object.assign({}, acc), this.createFont(font));
        }, { icons: icons || require('../../../Assets/Icons/icons.ttf') });
        allFonts = omit(allFonts, alreadyLoadedFonts);
        if (Object.keys(allFonts).length <= 0) {
            return Promise.resolve();
        }
        if (Platform.OS === 'web') {
            let html = '';
            fonts.forEach(font => {
                if (font.stylesheet) {
                    const el = document.createElement('link');
                    el.setAttribute('href', font.stylesheet);
                    el.setAttribute('rel', 'stylesheet');
                    document.head.appendChild(el);
                }
                else {
                    Object.entries(font).forEach(([key, value]) => {
                        if (key === 'name' || key === 'stylesheet') {
                            return;
                        }
                        html = `
${html}

@font-face {
    font-family: '${font.name}';
    font-style: ${key.includes('_italic') ? 'italic' : 'normal'};
    font-weight: ${this.getFontWeight(key)};
    font-display: swap;
    src: url(${value});
}
                        `;
                    });
                }
            });
            if (html) {
                const el = document.createElement('style');
                el.setAttribute('type', 'text/css');
                el.textContent = html.trim();
                document.body.appendChild(el);
            }
            return Font.loadAsync({
                icons: icons || require('../../../Assets/Icons/icons.ttf')
            })
                .then(res => {
                alreadyLoadedFonts = [...alreadyLoadedFonts, ...Object.keys(allFonts)];
                return res;
            });
        }
        return Font.loadAsync(allFonts)
            .then(res => {
            alreadyLoadedFonts = [...alreadyLoadedFonts, ...Object.keys(allFonts)];
            return res;
        });
    }
    getFontKey(name, weight) {
        return `${name.toLowerCase()}_${weight.toLowerCase()}`;
    }
    createFont(font) {
        const name = font.name.toLowerCase();
        const fontFamily = Object.entries(font).reduce((acc, [key, value]) => {
            if (key === 'name') {
                return acc;
            }
            if (key === 'stylesheet') {
                return acc;
            }
            acc[this.getFontKey(name, key)] = value;
            return acc;
        }, {});
        if (font.regular) {
            fontFamily[name] = font.regular;
        }
        return fontFamily;
    }
    showShareOptions(params) {
        return this.navigate(CoreRoutes.share, params);
    }
    showNotificationsPermissions(forceReset) {
        return this.navigate(CoreRoutes.notificationsPermissions, undefined, forceReset);
    }
    hideNotificationsPermissions() {
        return this.dismiss();
    }
    navigateToPushNotification(id) {
        return this.navigate(CoreRoutes.pushNotification, { id });
    }
    showToast(toast) {
        if (!toast.id) {
            toast.id = uuid();
        }
        if (!toast.type) {
            toast.type = 'success';
        }
        this.toasts = [...this.toasts, toast];
        if (!this.toastsInterval) {
            this.toastsInterval = setInterval(() => {
                this.toasts = this.toasts.slice(1);
                this.customNavigator.show(CoreRoutes.toast, this.toasts);
                if (this.toasts.length <= 0) {
                    this.customNavigator.hide(CoreRoutes.toast);
                    clearInterval(this.toastsInterval);
                    this.toastsInterval = undefined;
                }
            }, 3000);
        }
        return this.customNavigator.show(CoreRoutes.toast, this.toasts);
    }
    showError(params) {
        if (Platform.OS !== 'web') {
            let interactions = [...params.interactions || []];
            if (!interactions) {
                interactions = [
                    {
                        text: 'OK',
                        onPress: () => { }
                    }
                ];
            }
            Alert.alert(params.title || '', params.description || '', interactions === null || interactions === void 0 ? void 0 : interactions.map(interaction => ({
                text: interaction.text,
                onPress: () => { var _a; return (_a = interaction.onPress) === null || _a === void 0 ? void 0 : _a.call(interaction); }
            })));
            return Promise.resolve(this.getCurrentRoute());
        }
        return this.navigate(CoreRoutes.error, params);
    }
    hideError() {
        if (this.getCurrentRoute() !== CoreRoutes.error) {
            return this.getCurrentRoute();
        }
        return this.dismiss();
    }
    showPopUp(params) {
        if (Platform.OS !== 'web') {
            let interactions = [...params.interactions || []];
            if (!interactions) {
                interactions = [
                    {
                        text: 'OK',
                        onPress: () => { }
                    }
                ];
            }
            Alert.alert(params.title || '', params.description || '', interactions === null || interactions === void 0 ? void 0 : interactions.map(interaction => ({
                text: interaction.text,
                onPress: () => { var _a; return (_a = interaction.onPress) === null || _a === void 0 ? void 0 : _a.call(interaction); }
            })));
            return Promise.resolve(this.getCurrentRoute());
        }
        return this.navigate(CoreRoutes.popUp, params);
    }
    hidePopUp() {
        if (this.getCurrentRoute() !== CoreRoutes.popUp) {
            return this.getCurrentRoute();
        }
        return this.dismiss();
    }
    showLoading() {
        this.loaders++;
        if (this.loaders > 1) {
            return Promise.resolve(CoreRoutes.loading);
        }
        return this.navigate(CoreRoutes.loading);
    }
    hideLoading() {
        this.loaders--;
        if (this.loaders < 0) {
            this.loaders = 0;
        }
        if (this.loaders > 0) {
            return CoreRoutes.loading;
        }
        return this.dismiss();
    }
};
Coordinator = __decorate([
    injectable(),
    __param(0, inject(ICustomNavigatorType)),
    __metadata("design:paramtypes", [Object])
], Coordinator);
export { Coordinator };
